import React from 'react';
import {
  Box,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
import type { PaginationProps } from '@components/common/Pagination/interface';
import { useLanguage } from '../../../contexts/LanguageContext';
import translations from './translations.json';

type SupportedLanguage = keyof typeof translations;

const DARK_CARD_BG = '#121824';
const DARK_BORDER = '#1e2638';
const FIELD_BG = '#161d2b';
const ACCENT_COLOR = '#f59e0b'; // Couleur dorée / jaune active de la maquette

export const PaginationComponent: React.FC<PaginationProps> = ({
  currentPage,
  onPageChange,
  rowsPerPageOptions = [5, 10, 25, 50],
  rowsPerPage = 10,
  onRowsPerPageChange,
  totalItems,
}) => {
  const { language } = useLanguage();
  const content = translations[language as SupportedLanguage] || translations.en;
  const totalPages = Math.ceil(totalItems / rowsPerPage);

  return (
    <Box
      sx={{
        display: 'flex',
        justify: 'space-between',
        alignItems: 'center',
        mt: 4,
        pt: 2.5,
        pb: 1,
        px: 2,
        borderRadius: '12px',
        backgroundColor: DARK_CARD_BG,
        border: `1px solid ${DARK_BORDER}`,
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {rowsPerPage && onRowsPerPageChange && (
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel
              id="rows-per-page-label"
              sx={{ color: '#8c94a4 !important', fontSize: '0.85rem' }}
            >
              {content.perPage}
            </InputLabel>
            <Select
              labelId="rows-per-page-label"
              value={rowsPerPage}
              onChange={(e) => onRowsPerPageChange(e.target.value as number)}
              label={content.perPage}
              sx={{
                color: '#ffffff',
                backgroundColor: FIELD_BG,
                fontSize: '0.875rem',
                '& .MuiSvgIcon-root': {
                  color: '#8c94a4',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${DARK_BORDER} !important`,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3b82f6 !important',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3b82f6 !important',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: DARK_CARD_BG,
                    borderColor: DARK_BORDER,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    color: '#ffffff',
                    '& .MuiMenuItem-root': {
                      fontSize: '0.875rem',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(245, 158, 11, 0.2)',
                        color: ACCENT_COLOR,
                      },
                    },
                  },
                },
              }}
            >
              {rowsPerPageOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {totalItems !== undefined && (
          <Typography variant="body2" sx={{ color: '#8c94a4', fontSize: '0.875rem' }}>
            {content.total}: {totalItems} {content.items}
          </Typography>
        )}
      </Box>

      <Pagination
        count={totalPages}
        page={currentPage}
        onChange={(_, page) => onPageChange(page)}
        shape="rounded"
        sx={{
          '& .MuiPaginationItem-root': {
            borderColor: DARK_BORDER,
            color: '#8c94a4',
            backgroundColor: FIELD_BG,
            fontSize: '0.875rem',
            '&.Mui-selected': {
              backgroundColor: ACCENT_COLOR,
              borderColor: ACCENT_COLOR,
              color: '#000000',
              fontWeight: 700,
              '&:hover': {
                backgroundColor: 'primary.main',
              },
            },
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              color: '#ffffff',
            },
          },
        }}
      />
    </Box>
  );
};

export default PaginationComponent;